import { useDialogContext, useStoreState } from "@ariakit/react";
import { LetterheadSubmitError, Success, useForm } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { useState } from "react";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import type { Git } from "../../engine/Git.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import {
  useHandleSuccess,
  type SuccessReaction,
} from "../FormDialog/useHandleSuccess.ts";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import {
  FormCheckboxOption,
  FormOptions,
  FormTextField,
} from "../forms/FormTextField.tsx";
import { useGitContext } from "../GangPage/ModelContext.tsx";
import { PreviewBanner } from "../GangPage/StashPreviewBanner.tsx";
import { useGangContext } from "../GangPage/useGang.ts";
import { GenerateNameHint } from "./GenerateNameHint.tsx";
import { GitTypeFormOption } from "./GitTypeFormOption.tsx";

type OnSubmit = (
  data: Pick<SpaceGits.GitData, "name" | "type">,
  isDiscounted: boolean,
) => Promise<Success<SuccessReaction>>;

function GitFormDialog(props: {
  git?: Git;
  heading: string;
  gnawballHeading?: string;
  overline?: string;
  submit: string;
  onSubmit: OnSubmit;
}) {
  const { gang } = useGangContext();

  const {
    git,
    overline,
    submit,
    heading: defaultHeading,
    gnawballHeading = defaultHeading,
    onSubmit: handleSubmit,
  } = props;

  const dialog = useDialogContext();
  const handleSuccess = useHandleSuccess(dialog);
  const { ruleset, mode } = gang;
  const gitProfiles = ruleset.gits.toArray();
  const { orcNameGenerator, gnawballNameGenerator } = ruleset;
  const [gitDefaultName] = useState(() => orcNameGenerator.generate());
  const [gnawballDefaultName] = useState(() =>
    gnawballNameGenerator.generate(),
  );

  const defaultValues = {
    name: git?.data.name ?? "",
    typeId: git?.data.type.id ?? gitProfiles.at(0)?.id ?? "",
    isDiscounted: !mode.isCampaignMode,
  };

  const { form, submitName } = useForm({
    defaultValues: defaultValues,
    async onSubmit({ values }) {
      const shouldBeNamed = !git && !values.name;
      const isGnawball = values.typeId === "gnawball";
      const name = shouldBeNamed
        ? isGnawball
          ? gnawballDefaultName
          : gitDefaultName
        : values.name;

      return await handleSubmit(
        {
          name: name,
          type: { id: values.typeId },
        },
        values.isDiscounted,
      );
    },

    async onSuccess(values) {
      await handleSuccess(values);
    },
  });

  const values = useStoreState(form, (form) => form.values);

  const isGnawball = values.typeId === "gnawball";
  const heading = isGnawball ? gnawballHeading : defaultHeading;
  const generator = isGnawball ? gnawballNameGenerator : orcNameGenerator;
  const placeholder = isGnawball ? gnawballDefaultName : gitDefaultName;

  return (
    <FormDialog heading={heading} overline={overline} form={form}>
      {!git && (
        <PreviewBanner
          draftGang={
            gang.addGit(
              { type: { id: values.typeId }, name: "" },
              values.isDiscounted,
            ).gang
          }
          where={"on the streets of Gnork"}
        />
      )}

      <FormTextField
        name={form.names.name}
        label="Name"
        placeholder={`E.g. ${placeholder}`}
        autoFocusOnWide
        hint={
          <GenerateNameHint
            generator={generator}
            onGenerate={(name) => void form.setValue(form.names.name, name)}
          />
        }
      />

      {!git && mode.isCampaignMode && (
        <FormOptions label="Campaign">
          <FormCheckboxOption
            label="Enable pity points"
            description="If you're currently the gang with the lowest reputation in your campaign, you use a discounted rate."
            name={form.names.isDiscounted}
            type="checkbox"
          />
        </FormOptions>
      )}

      {(!git || git.can(EditMode.CHANGE, "models")) && (
        <FormOptions label="Type">
          {gitProfiles.map((profile) => {
            return (
              <GitTypeFormOption
                type="radio"
                key={profile.id}
                name={form.names.typeId}
                profile={profile}
                priceMultiplier={values.isDiscounted ? 1 : 2}
              />
            );
          })}
        </FormOptions>
      )}

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />

        <FormDialogSubmitButton>{submit}</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}

export function CreateGitDialog() {
  const { gang } = useGangContext();

  return (
    <GitFormDialog
      overline="City of Gnork"
      heading="ID Registration Form"
      gnawballHeading="Pet Registration Form"
      submit="Add"
      onSubmit={async (data, isDiscounted) => {
        const git = gang.addGit(data, isDiscounted);
        const result = await database.gangs.updateOne(git.gang.data);
        return result.mapSuccess((gangId) => ({
          navigateTo: `~/gangs/g/${gangId}/g/${git.id}`,
        }));
      }}
    />
  );
}

export function EditGitDialog() {
  const git = useGitContext();

  return (
    <GitFormDialog
      git={git}
      heading="Edit git"
      submit="Save"
      onSubmit={async (data) => {
        const nextGit = git.clone(data);
        const result = await database.gangs.updateOne(nextGit.gang.data);
        return result.mapSuccess(() => ({
          message: "Saved",
        }));
      }}
    />
  );
}
